import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Alert,
  IconButton,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  Typography,
  Checkbox,
  MenuItem,
  ListItemText,
  List,
  ListItem,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Publish as PublishIcon,
  ArrowLeft,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import { trainingClient } from '../../../app/services/api/compliance/training';
import {
  TrainingModuleDto,
  TrainingCategoryDto,
  TrainingFranchiseOptionDto,
  TrainingBundleType,
  CreateTrainingBundleDto,
  UpdateTrainingBundleDto,
} from '../../../app/services/api/compliance/training/trainingTypes';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { UserContext } from '../../../components/shared/useUser';

interface TrainingBundleFormState {
  id?: number;
  name: string;
  description: string;
  franchiseIds: number[];
  categoryId: number;
  categoryName: string;
  bundleType: TrainingBundleType;
  status: number;
  moduleIds: number[];
}

const TrainingBundleBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { bundleId } = useParams<{ bundleId?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { t } = useTranslation('training');
  const { selectedFranchise } = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [franchises, setFranchises] = useState<TrainingFranchiseOptionDto[]>([]);
  const [categories, setCategories] = useState<TrainingCategoryDto[]>([]);
  const [availableModules, setAvailableModules] = useState<TrainingModuleDto[]>([]);
  // Names of modules already on the bundle that may no longer be in availableModules (e.g. archived).
  const [moduleNameOverrides, setModuleNameOverrides] = useState<Record<number, string>>({});

  const [bundle, setBundle] = useState<TrainingBundleFormState>({
    name: '',
    description: '',
    franchiseIds: [],
    categoryId: 0,
    categoryName: '',
    bundleType: TrainingBundleType.Course,
    status: 0,
    moduleIds: [],
  });

  useEffect(() => {
    loadFranchises();
    loadCategories();
    loadModules();
    if (bundleId) {
      loadBundle();
    }
  }, [bundleId]);

  const loadFranchises = async () => {
    try {
      const contextFranchiseId = selectedFranchise?.franchiseId;
      const data = await trainingClient.getAccessibleFranchises(contextFranchiseId);
      setFranchises(data);
      if (!bundleId && data.length > 0) {
        if (data.length === 1) {
          setBundle(prev => ({ ...prev, franchiseIds: [data[0].id] }));
        } else if (contextFranchiseId && data.some(f => f.id === contextFranchiseId)) {
          setBundle(prev => ({ ...prev, franchiseIds: [contextFranchiseId] }));
        }
      }
    } catch {
      setError(t('bundleBuilder.failedToLoadFranchises'));
    }
  };

  const loadCategories = async () => {
    try {
      const data = await trainingClient.getCategories();
      setCategories(data);
    } catch {
      setError(t('bundleBuilder.failedToLoadCategories'));
    }
  };

  const loadModules = async () => {
    try {
      const franchiseId = selectedFranchise?.franchiseId || 0;
      const data = await trainingClient.getModules({ franchiseId, status: 1, includeInactive: false });
      setAvailableModules(data);
    } catch {
      setError(t('bundleBuilder.failedToLoadModules'));
    }
  };

  const loadBundle = async () => {
    try {
      setLoading(true);
      const data = await trainingClient.getBundle(parseInt(bundleId!));
      if (data) {
        setBundle({
          id: data.id,
          name: data.name,
          description: data.description || '',
          franchiseIds: data.franchiseIds || [],
          categoryId: data.categoryId,
          categoryName: data.categoryName || '',
          bundleType: data.bundleType,
          status: data.status,
          moduleIds: data.modules.map(m => m.moduleId),
        });
        setModuleNameOverrides(Object.fromEntries(data.modules.map(m => [m.moduleId, m.moduleName])));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bundleBuilder.failedToLoadBundle'));
    } finally {
      setLoading(false);
    }
  };

  const getModuleName = (moduleId: number): string =>
    availableModules.find(m => m.id === moduleId)?.name || moduleNameOverrides[moduleId] || `#${moduleId}`;

  const handleAddModule = (moduleId: number) => {
    if (bundle.moduleIds.includes(moduleId)) return;
    setBundle({ ...bundle, moduleIds: [...bundle.moduleIds, moduleId] });
  };

  const handleRemoveModule = (moduleId: number) => {
    setBundle({ ...bundle, moduleIds: bundle.moduleIds.filter(id => id !== moduleId) });
  };

  const handleMoveModule = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= bundle.moduleIds.length) return;
    const reordered = [...bundle.moduleIds];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    setBundle({ ...bundle, moduleIds: reordered });
  };

  const handleSaveBundle = async () => {
    if (!bundle.name.trim()) {
      setError(t('bundleBuilder.bundleNameRequired'));
      return;
    }
    if (!bundle.franchiseIds.length) {
      setError(t('bundleBuilder.franchiseRequired'));
      return;
    }
    if (!bundle.categoryId) {
      setError(t('bundleBuilder.categoryRequired'));
      return;
    }
    if (bundle.moduleIds.length < 2) {
      setError(t('bundleBuilder.modulesRequired'));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (bundleId) {
        const updateDto: UpdateTrainingBundleDto = {
          categoryId: bundle.categoryId,
          name: bundle.name,
          description: bundle.description || null,
          status: bundle.status,
          franchiseIds: bundle.franchiseIds,
          moduleIds: bundle.moduleIds,
        };
        await trainingClient.updateBundle(parseInt(bundleId), updateDto);
        setSuccess(t('bundleBuilder.bundleUpdated'));
        await loadBundle();
      } else {
        const createDto: CreateTrainingBundleDto = {
          categoryId: bundle.categoryId,
          name: bundle.name,
          description: bundle.description || null,
          bundleType: bundle.bundleType,
          franchiseIds: bundle.franchiseIds,
          moduleIds: bundle.moduleIds,
        };
        const newId = await trainingClient.createBundle(createDto);
        setSuccess(t('bundleBuilder.bundleCreated'));
        setTimeout(() => navigate(`/compliance/training/courses-packs/builder/${newId}`), 800);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bundleBuilder.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!bundleId) return;
    try {
      setSaving(true);
      setError(null);
      await trainingClient.publishBundle(parseInt(bundleId));
      setSuccess(t('bundleBuilder.bundlePublished'));
      await loadBundle();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('bundleBuilder.failedToPublish'));
    } finally {
      setSaving(false);
    }
  };

  const unselectedModules = availableModules.filter(m => !bundle.moduleIds.includes(m.id));

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={t('bundleBuilder.subtitle')}>
          {bundleId ? t('bundleBuilder.editTitle') : t('bundleBuilder.createTitle')}
        </PageTitle>
        <Stack direction="row"
          spacing={2}>
          <Button
            variant="secondary"
            size="lg"
            startIcon={<SvgIcon><ArrowLeft /></SvgIcon>}
            onClick={() => navigate('/compliance/training/courses-packs')}
          >
            {t('bundleBuilder.back')}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            startIcon={<SaveIcon />}
            onClick={handleSaveBundle}
            disabled={saving}
          >
            {t('bundleBuilder.saveDraft')}
          </Button>
          {bundleId && bundle.status !== 1 && (
            <Button
              variant="primary"
              size="lg"
              startIcon={<PublishIcon />}
              onClick={handlePublish}
              disabled={saving}
            >
              {t('bundleBuilder.publish')}
            </Button>
          )}
        </Stack>
      </Stack>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}

      <Section style={{ p: 5, mb: 5, pb: 5 }}>
        <Box sx={{ fontSize: '1rem', fontWeight: 500, mb: 3 }}>
          {t('bundleBuilder.bundleDetails')}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            fullWidth
            label={t('bundleBuilder.bundleName')}
            value={bundle.name}
            onChange={(e) => setBundle({ ...bundle, name: e.target.value })}
            required
          />
          <TextField
            fullWidth
            select
            label={t('bundleBuilder.bundleType')}
            value={bundle.bundleType}
            onChange={(e) => setBundle({ ...bundle, bundleType: Number(e.target.value) as TrainingBundleType })}
            required
            disabled={!!bundleId}
          >
            <MenuItem value={TrainingBundleType.Course}>{t('bundleBuilder.course')}</MenuItem>
            <MenuItem value={TrainingBundleType.Pack}>{t('bundleBuilder.pack')}</MenuItem>
          </TextField>
          <TextField
            fullWidth
            select
            label={t('bundleBuilder.franchise')}
            value={bundle.franchiseIds}
            onChange={(e) => {
              const value = e.target.value as unknown as number[];
              if (value.includes(-1)) {
                const allIds = franchises.map(f => f.id);
                const isAllSelected = bundle.franchiseIds.length === franchises.length;
                setBundle({ ...bundle, franchiseIds: isAllSelected ? [] : allIds });
              } else {
                setBundle({ ...bundle, franchiseIds: value });
              }
            }}
            required
            SelectProps={{
              multiple: true,
              renderValue: (selected) => {
                const ids = selected as number[];
                if (ids.length === 0) return '';
                if (ids.length === franchises.length) return t('trainingCategories.allFranchises');
                return ids.map(id => franchises.find(f => f.id === id)?.name || id).join(', ');
              },
            }}
          >
            <MenuItem value={-1}>
              <Checkbox
                checked={bundle.franchiseIds.length === franchises.length && franchises.length > 0}
                indeterminate={bundle.franchiseIds.length > 0 && bundle.franchiseIds.length < franchises.length}
              />
              <ListItemText primary={t('trainingCategories.selectAll')} />
            </MenuItem>
            {franchises.map((franchise) => (
              <MenuItem key={franchise.id}
                value={franchise.id}>
                <Checkbox checked={bundle.franchiseIds.includes(franchise.id)} />
                <ListItemText primary={franchise.name} />
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label={t('bundleBuilder.trainingCategory')}
            value={bundle.categoryId || ''}
            onChange={(e) => {
              const categoryId = parseInt(e.target.value as string);
              const category = categories.find(c => c.id === categoryId);
              setBundle({ ...bundle, categoryId, categoryName: category?.name || '' });
            }}
            required
          >
            <MenuItem value=""><em>&nbsp;</em></MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id}
                value={category.id}>
                {category.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('bundleBuilder.description')}
            value={bundle.description}
            onChange={(e) => setBundle({ ...bundle, description: e.target.value })}
          />
        </Box>
      </Section>

      <Section style={{ p: 5, mb: 5 }}>
        <Box sx={{ fontSize: '1rem', fontWeight: 500, mb: 3 }}>
          {t('bundleBuilder.modulesSectionTitle')}
        </Box>
        <Box
          display="flex"
          gap={4}
          flexWrap="wrap"
        >
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography
              component="div"
              variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {t('bundleBuilder.availableModules')}
            </Typography>
            {unselectedModules.length === 0 ? (
              <Typography
                component="div"
                variant="textSM"
                color="text.secondary"
              >
                {t('bundleBuilder.noModulesAvailable')}
              </Typography>
            ) : (
              <List dense>
                {unselectedModules.map((module) => (
                  <ListItem
                    key={module.id}
                    divider
                  >
                    <ListItemText primary={module.name} />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handleAddModule(module.id)}
                        title={t('bundleBuilder.availableModules')}
                      >
                        <AddIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Divider
            orientation="vertical"
            flexItem
          />

          <Box sx={{ flex: 1, minWidth: 280 }}>
            <Typography
              component="div"
              variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}
            >
              {t(
                bundle.bundleType === TrainingBundleType.Course
                  ? 'bundleBuilder.selectedModules'
                  : 'bundleBuilder.selectedModulesNoOrder',
              )}
            </Typography>
            {bundle.moduleIds.length === 0 ? (
              <Typography
                component="div"
                variant="textSM"
                color="text.secondary"
              >
                {t('bundleBuilder.noModulesSelected')}
              </Typography>
            ) : (
              <List dense>
                {bundle.moduleIds.map((moduleId, index) => (
                  <ListItem
                    key={moduleId}
                    divider
                  >
                    <ListItemText
                      primary={
                        bundle.bundleType === TrainingBundleType.Course
                          ? `${index + 1}. ${getModuleName(moduleId)}`
                          : getModuleName(moduleId)
                      }
                    />
                    <ListItemSecondaryAction>
                      {bundle.bundleType === TrainingBundleType.Course && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveModule(index, -1)}
                            disabled={index === 0}
                            title={t('bundleBuilder.moveUp')}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleMoveModule(index, 1)}
                            disabled={index === bundle.moduleIds.length - 1}
                            title={t('bundleBuilder.moveDown')}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveModule(moduleId)}
                        title={t('bundleBuilder.remove')}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Section>
    </PageArea>
  );
};

export default TrainingBundleBuilderPage;
